import { Box, Stack } from '@mui/material'
import moment from 'moment'
import { FC, Ref, UIEventHandler } from 'react'
import { ShiftCell } from './ShiftCell'
import {
	getDatesInRange,
	getMaxAssignmentCountInRow,
	getShiftScheduleColumnsSx,
	getShiftScheduleGridWidth,
	isPastScheduleDate,
	SHIFT_CELL_ADD_PLATE_HEIGHT,
	shouldStretchShiftColumns,
	sortCellAssignments,
} from './shiftScheduleLayout'

interface ShiftScheduleEmployee {
	id: number | string
}

export interface ShiftCellAssignment {
	id: number
	milkerId: number
	date: number
	title: string
	timeRange: string
	parlorName: string
	color: string
	zones?: number[]
}

interface ShiftScheduleGridProps {
	dateRange: { startDate: number; endDate: number }
	columnWidth?: number
	employees?: ShiftScheduleEmployee[]
	scrollRef?: Ref<HTMLDivElement>
	onScroll?: UIEventHandler<HTMLDivElement>
	onAssignmentClick?: (payload: {
		employeeId: number
		date: number
		assignment: ShiftCellAssignment
		assignments: ShiftCellAssignment[]
	}) => void
	onAddClick?: (payload: {
		employeeId: number
		date: number
		assignments: ShiftCellAssignment[]
	}) => void
	assignments?: ShiftCellAssignment[]
	selectedCellKey?: string | null
	employeeRowHeights?: Map<number, number>
}

export const ShiftScheduleGrid: FC<ShiftScheduleGridProps> = ({
	dateRange,
	columnWidth,
	employees,
	onAssignmentClick,
	onAddClick,
	assignments = [],
	selectedCellKey,
	employeeRowHeights,
}) => {
	const dates = getDatesInRange(dateRange.startDate, dateRange.endDate)
	const stretchColumns = shouldStretchShiftColumns(dates.length)
	const columnsSx = getShiftScheduleColumnsSx(dates.length, columnWidth)
	const gridWidth = getShiftScheduleGridWidth(dates.length, columnWidth)
	const assignmentMap = new Map<string, ShiftCellAssignment[]>()
	const assignmentCountByCellKey = new Map<string, number>()

	assignments.forEach(item => {
		const cellDate = moment(item.date).startOf('day').valueOf()
		const key = `${item.milkerId}-${cellDate}`
		const prev = assignmentMap.get(key) ?? []
		assignmentMap.set(key, [...prev, item])
	})

	assignmentMap.forEach((items, key) => {
		assignmentCountByCellKey.set(key, items.length)
	})

	if (!employees?.length) return null

	return (
		<Stack
			direction='column'
			gap='10px'
			sx={{
				flexShrink: 0,
				width: stretchColumns ? '100%' : `${gridWidth}px`,
				minWidth: stretchColumns ? '100%' : `${gridWidth}px`,
				pb: `${SHIFT_CELL_ADD_PLATE_HEIGHT}px`,
			}}
		>
			{employees.map(employee => {
				const normalizedDates = dates.map(date =>
					date.clone().startOf('day').valueOf(),
				)
				const slotCount = getMaxAssignmentCountInRow(
					employee.id,
					normalizedDates,
					assignmentCountByCellKey,
				)
				const rowHeight = employeeRowHeights?.get(Number(employee.id)) ?? 70

				return (
					<Box
						key={employee.id}
						sx={{
							...columnsSx,
							alignItems: 'flex-start',
							minHeight: `${rowHeight}px`,
						}}
					>
						{dates.map(date => {
							const normalizedDate = date.clone().startOf('day').valueOf()
							const cellKey = `${Number(employee.id)}-${normalizedDate}`
							const cellAssignments = sortCellAssignments(
								assignmentMap.get(cellKey) ?? [],
							)
							const isPastDate = isPastScheduleDate(normalizedDate)

							return (
								<ShiftCell
									key={date.valueOf()}
									slotCount={slotCount}
									isPastDate={isPastDate}
									assignments={cellAssignments}
									selected={selectedCellKey === cellKey}
									onAssignmentClick={assignment =>
										onAssignmentClick?.({
											employeeId: Number(employee.id),
											date: normalizedDate,
											assignment,
											assignments: cellAssignments,
										})
									}
									onAddClick={() =>
										onAddClick?.({
											employeeId: Number(employee.id),
											date: normalizedDate,
											assignments: cellAssignments,
										})
									}
								/>
							)
						})}
					</Box>
				)
			})}
		</Stack>
	)
}
